/* eslint-disable react-hooks/refs */
/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-hooks/purity */
import { useEffect, useRef, useCallback, useState } from 'react';

import type { SystemNotice } from '@tankef/core';
import { config, logger } from '@tankef/env';

import { useNoticesStore, useToastStore, useSessionStore, type Toast } from '../stores';

// ============================================================================
// NOTICES HOOKS
// ============================================================================

export function useActiveNotices(): SystemNotice[] {
  const notices = useNoticesStore(s => s.notices);
  const dismissedIds = useNoticesStore(s => s.dismissedIds);

  return notices.filter(notice => notice.isActive() && !dismissedIds.has(notice.id));
}

export function useDismissNotice(): (id: string) => void {
  return useNoticesStore(s => s.dismiss);
}

// ============================================================================
// TOAST HOOKS
// ============================================================================

export interface ToastMethods {
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  custom: (toast: Omit<Toast, 'id'>) => string;
  remove: (id: string) => void;
  clear: () => void;
}

export function useToast(): ToastMethods {
  const addToast = useToastStore(s => s.addToast);
  const removeToast = useToastStore(s => s.removeToast);
  const clearToasts = useToastStore(s => s.clearToasts);

  return {
    success: useCallback(
      (title: string, message?: string) => addToast({ type: 'success', title, message }),
      [addToast]
    ),
    error: useCallback(
      (title: string, message?: string) => addToast({ type: 'error', title, message }),
      [addToast]
    ),
    warning: useCallback(
      (title: string, message?: string) => addToast({ type: 'warning', title, message }),
      [addToast]
    ),
    info: useCallback(
      (title: string, message?: string) => addToast({ type: 'info', title, message }),
      [addToast]
    ),
    custom: useCallback((toast: Omit<Toast, 'id'>) => addToast(toast), [addToast]),
    remove: removeToast,
    clear: clearToasts,
  };
}

// ============================================================================
// INACTIVITY TIMEOUT HOOK
// ============================================================================

const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'mousemove',
  'click',
  'wheel',
];

export interface InactivityTimeoutResult {
  resetTimers: () => void;
  lastActivity: number;
}

export function useInactivityTimeout(onTimeout: () => void): InactivityTimeoutResult {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const lastActivityRef = useRef<number>(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  // Hold a stable ref to setWarning so we never re-create resetTimers when the store selector changes
  const setWarningRef = useRef(useSessionStore.getState().setWarning);
  useEffect(() => {
    return useSessionStore.subscribe(s => {
      setWarningRef.current = s.setWarning;
    });
  }, []);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();

    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);

    // Dismiss any active warning as soon as the user is active again
    setWarningRef.current(false);

    // Show warning `sessionWarningBefore` ms before the inactivity timeout fires
    warningRef.current = setTimeout(() => {
      setWarningRef.current(true, config.sessionWarningBefore);
    }, config.inactivityTimeout - config.sessionWarningBefore);

    timeoutRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, config.inactivityTimeout);
  }, []);

  useEffect(() => {
    // Clear stale warning left from a previous session (e.g. user logged out while warning was open)
    setWarningRef.current(false);

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetTimers, { passive: true });
    });

    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimers);
      });
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [resetTimers]);

  return {
    resetTimers,
    lastActivity: lastActivityRef.current,
  };
}

// ============================================================================
// SESSION WARNING HOOK
// ============================================================================

export interface SessionWarningResult {
  showWarning: boolean;
  remainingTime: number;
  onExtend: () => void;
}

// useSessionWarning reads from the store that useInactivityTimeout writes to.
// It only drives the countdown display and wires "Continuar sesión" → resetTimers.
export function useSessionWarning(resetTimers: () => void): SessionWarningResult {
  const { showWarning, remainingTime, setWarning } = useSessionStore();
  const [countdown, setCountdown] = useState(0);

  // Drive the countdown tick while the warning is visible
  useEffect(() => {
    if (!showWarning) {
      setCountdown(0);
      return;
    }

    setCountdown(remainingTime);

    const id = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(id);
  }, [showWarning, remainingTime]);

  // "Continuar sesión" → dismiss modal + reset both inactivity timers (warning + logout)
  const handleExtend = useCallback(() => {
    setWarning(false);
    resetTimers();
  }, [resetTimers, setWarning]);

  return {
    showWarning,
    remainingTime: countdown,
    onExtend: handleExtend,
  };
}

// ============================================================================
// DEBOUNCE HOOK
// ============================================================================

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================================================
// PREVIOUS VALUE HOOK
// ============================================================================

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

// ============================================================================
// INTERVAL HOOK
// ============================================================================

export function useInterval(callback: () => void, delay: number | null): void {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const tick = () => savedCallback.current();
    const id = setInterval(tick, delay);

    return () => clearInterval(id);
  }, [delay]);
}

// ============================================================================
// LOCAL STORAGE HOOK
// ============================================================================

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        logger.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue];
}

// ============================================================================
// MEDIA QUERY HOOK
// ============================================================================

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQuery.addEventListener('change', handler);
    setMatches(mediaQuery.matches);

    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// Convenience hooks for common breakpoints
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 639px)');
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 640px) and (max-width: 1023px)');
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}
